import { format, parseISO, isValid } from 'date-fns';

/**
 * Recipe Pin Transformer
 *
 * Transforms recipe execution data into pinned route format
 * with concise labels and metadata for quick navigation.
 */

/**
 * Shorten recipe name intelligently
 * Preserves meaningful words, removes filler
 *
 * @param {string} name - Full recipe name
 * @param {number} maxLength - Maximum length for shortened name
 * @returns {string} Shortened name
 */
export const shortenRecipeName = (name, maxLength = 30) => {
  if (!name) return 'Untitled Recipe';
  if (name.length <= maxLength) return name;

  // Remove common filler words
  const fillers = ['Dashboard', 'Analysis', 'Report', 'the', 'and', 'or'];
  let shortened = name;

  for (const filler of fillers) {
    const regex = new RegExp(`\\s${filler}\\s`, 'gi');
    shortened = shortened.replace(regex, ' ');
  }

  // Trim to max length at word boundary
  if (shortened.length > maxLength) {
    shortened = shortened.substring(0, maxLength);
    const lastSpace = shortened.lastIndexOf(' ');
    if (lastSpace > maxLength * 0.7) {
      shortened = shortened.substring(0, lastSpace);
    }
    shortened += '...';
  }

  return shortened.trim();
};

/**
 * Format execution date for display
 *
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date (e.g., "Jan 15" or "Yesterday")
 */
export const formatExecutionDate = (dateString) => {
  if (!dateString) return '';

  try {
    const date = parseISO(dateString);
    if (!isValid(date)) return '';

    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return format(date, 'MMM d');
  } catch (error) {
    console.error('Error formatting execution date:', error);
    return '';
  }
};

/**
 * Format execution duration
 *
 * @param {number} durationMs - Duration in milliseconds
 * @returns {string} Formatted duration (e.g., "2m 30s" or "45s")
 */
export const formatExecutionDuration = (durationMs) => {
  if (!durationMs || durationMs < 0) return '';

  const seconds = Math.floor(durationMs / 1000);
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (remainingSeconds === 0) return `${minutes}m`;
  return `${minutes}m ${remainingSeconds}s`;
};

/**
 * Get status indicator icon/text
 *
 * @param {string} status - Execution status
 * @returns {string} Status indicator
 */
export const getStatusIndicator = (status) => {
  const indicators = {
    completed: '✓',
    failed: '✗',
    stopped: '■',
    running: '▶',
  };
  return indicators[status] || '';
};

/**
 * Format execution summary for secondary line
 * Shows: date • step count • duration
 *
 * @param {Object} execution - Execution data
 * @returns {string} Summary line
 */
export const formatExecutionSummary = (execution) => {
  if (!execution) return '';

  const parts = [];

  // Date
  const date = formatExecutionDate(execution.completed_at || execution.started_at);
  if (date) parts.push(date);

  // Status
  const statusIndicator = getStatusIndicator(execution.status);
  if (statusIndicator) {
    parts.push(statusIndicator);
  } else if (execution.status) {
    parts.push(execution.status);
  }

  // Step count
  if (execution.step_count || execution.total_steps) {
    const count = execution.step_count || execution.total_steps;
    parts.push(`${count} ${count === 1 ? 'step' : 'steps'}`);
  }

  return parts.join(' • ');
};

/**
 * Generate unique pin ID for recipe
 *
 * @param {string} recipeId - Recipe UUID
 * @returns {string} Unique pin ID
 */
export const generateRecipePinId = (recipeId) => {
  return `recipe-pin-${recipeId}`;
};

/**
 * Parse recipe pin ID to extract recipe ID
 *
 * @param {string} pinId - Pin ID to parse
 * @returns {string|null} recipeId or null if invalid
 */
export const parseRecipePinId = (pinId) => {
  if (!pinId || !pinId.startsWith('recipe-pin-')) return null;
  return pinId.replace('recipe-pin-', '');
};

/**
 * Format last run summary for recipe pin
 *
 * @param {string} lastExecutionAt - ISO date string
 * @param {string} lastExecutionStatus - Execution status (not used, kept for backwards compatibility)
 * @returns {string} Summary line
 */
export const formatLastRunSummary = (lastExecutionAt, lastExecutionStatus) => {
  if (lastExecutionAt) {
    const date = formatExecutionDate(lastExecutionAt);
    if (date) return `Last run: ${date}`;
  }

  return 'No runs yet';
};

/**
 * Transform recipe pin data to route format
 * Compatible with PinnedRoutes component
 *
 * @param {Object} pinData - Pin metadata from user preferences
 * @param {Object} recipe - Recipe data (optional, for fresh name)
 * @param {Object} latestExecution - Latest execution data (optional, for fresh status)
 * @returns {Object} Route object for PinnedRoutes
 */
export const transformRecipePinToRoute = (pinData, recipe = null, latestExecution = null) => {
  // Use fresh data if available, fall back to pinned metadata
  const recipeName = recipe?.name || pinData.recipeName || 'Unknown Recipe';
  const lastExecutionAt = latestExecution?.completed_at || pinData.lastExecutionAt;
  const lastExecutionStatus = latestExecution?.status || pinData.lastExecutionStatus;

  // Generate labels - use shorter max length for sidebar display (22 chars)
  const shortenedName = shortenRecipeName(recipeName, 22);
  const summary = formatLastRunSummary(lastExecutionAt, lastExecutionStatus);
  const recipeCategory = pinData.recipeCategory || recipe?.category;
  const tags = [...new Set([
    'recipe',
    'recipes',
    'recipe result',
    'recipe results',
    recipeCategory,
  ].filter(Boolean))];
  const searchAliases = [...new Set([
    recipeName,
    `${recipeName} recipe`,
    `${recipeName} results`,
    `${recipeName} recipe results`,
  ])];

  return {
    id: pinData.id || generateRecipePinId(pinData.recipeId),
    label: shortenedName,
    sublabel: summary,
    category: 'Recipes',
    description: `Pinned recipe result · ${summary}`,
    tags,
    searchAliases,
    path: `/recipe-results/${pinData.recipeId}`, // Will be resolved to latest execution on click
    type: 'recipe-pin',
    metadata: {
      recipeId: pinData.recipeId,
      recipeName,
      recipeCategory,
      lastExecutionAt,
      lastExecutionStatus,
      pinnedAt: pinData.pinnedAt,
    },
  };
};

/**
 * Create pin data object from recipe
 * Used when user clicks "Pin" button
 *
 * @param {Object} recipe - Recipe object
 * @param {Object} latestExecution - Latest execution object (optional)
 * @returns {Object} Pin data to store in user preferences
 */
export const createRecipePinData = (recipe, latestExecution = null) => {
  return {
    id: generateRecipePinId(recipe.recipe_id),
    recipeId: recipe.recipe_id,
    recipeName: recipe.name,
    recipeCategory: recipe.category,
    lastExecutionAt: latestExecution?.completed_at || null,
    lastExecutionStatus: latestExecution?.status || null,
    pinnedAt: new Date().toISOString(),
  };
};

/**
 * Check if a recipe is pinned
 *
 * @param {Array} pinnedRecipes - Array of pinned recipes
 * @param {string} recipeId - Recipe ID to check
 * @returns {boolean} True if pinned
 */
export const isRecipePinned = (pinnedRecipes, recipeId) => {
  if (!pinnedRecipes || !Array.isArray(pinnedRecipes)) {
    return false;
  }

  return pinnedRecipes.some((pin) => pin.recipeId === recipeId);
};

/**
 * Find pin data for a recipe
 *
 * @param {Array} pinnedRecipes - Array of pinned recipes
 * @param {string} recipeId - Recipe ID
 * @returns {Object|null} Pin data or null if not found
 */
export const findRecipePin = (pinnedRecipes, recipeId) => {
  if (!pinnedRecipes || !Array.isArray(pinnedRecipes)) {
    return null;
  }

  return pinnedRecipes.find((pin) => pin.recipeId === recipeId) || null;
};

/**
 * Find latest successful execution ID for a recipe from recent executions
 *
 * @param {Array} recentExecutions - Array of recent executions
 * @param {string} recipeId - Recipe ID to find execution for
 * @returns {string|null} Latest successful execution ID or null
 */
export const findLatestExecutionId = (recentExecutions, recipeId) => {
  if (!recentExecutions || !Array.isArray(recentExecutions)) {
    return null;
  }

  // Filter to this recipe's SUCCESSFUL executions only and sort by completion date descending
  const recipeExecutions = recentExecutions
    .filter((exec) => exec.recipe_id === recipeId && exec.status === 'completed')
    .sort((a, b) => {
      const dateA = new Date(a.completed_at || a.started_at || 0);
      const dateB = new Date(b.completed_at || b.started_at || 0);
      return dateB - dateA;
    });

  return recipeExecutions[0]?.execution_id || null;
};

export default {
  shortenRecipeName,
  formatExecutionDate,
  formatExecutionDuration,
  getStatusIndicator,
  formatExecutionSummary,
  formatLastRunSummary,
  generateRecipePinId,
  parseRecipePinId,
  transformRecipePinToRoute,
  createRecipePinData,
  isRecipePinned,
  findRecipePin,
  findLatestExecutionId,
};
